import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { useSetupFlow } from '@up2promo-setup-frontend/core/hooks/use-setup-flow';
import { usesUrlSetupFlow } from '@up2promo-setup-frontend/core/constants/setup-product-types';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';

/**
 * Helper hook for setup navigation.
 * Provides navigation functions and route utilities.
 */
export function useSetupNavigation() {
  const navigate = useNavigate();
  const { hash } = useParams<{ hash: string }>();
  const [searchParams] = useSearchParams();
  const type = useSetupStore((state) => state.type);
  const { initialType } = useSetupFlow();

  const basePath = useMemo(() => {
    return hash ? `/${hash}` : '';
  }, [hash]);

  // Use the serialised string (primitive) as memo dep instead of the
  // URLSearchParams object. React Router v7 returns a new object reference on
  // every render; on iOS JavaScriptCore that alone is enough to keep the memo
  // invalidating and trigger a tight re-render loop until the call stack
  // overflows (Sentry SETUP-2, 401 events, 100 % iOS).
  const searchString = searchParams.toString();

  const navigateTo = useMemo(() => {
    const getPathWithSearch = (path: string) => {
      return searchString ? `${path}?${searchString}` : path;
    };

    return {
      welcome: () => navigate(getPathWithSearch(`${basePath}/welcome`)),
      email: () => navigate(getPathWithSearch(`${basePath}/email`)),
      selectType: () => navigate(getPathWithSearch(`${basePath}/select-type`)),
      input: () => navigate(getPathWithSearch(`${basePath}/input`)),
      address: () => navigate(getPathWithSearch(`${basePath}/address`)),
      selectSuggestion: () =>
        navigate(getPathWithSearch(`${basePath}/select-suggestion`)),
      customUrl: () => navigate(getPathWithSearch(`${basePath}/custom-url`)),
      congrats: () => navigate(getPathWithSearch(`${basePath}/congrats`)),
    };
  }, [navigate, basePath, searchString]);

  /**
   * Navigate to next step based on current flow state
   */
  const goNext = (fromRoute: string) => {
    switch (fromRoute) {
      case 'welcome':
        navigateTo.email();
        break;
      case 'email':
        // Navigate to select-type if no initialType
        if (!initialType) {
          navigateTo.selectType();
        } else {
          // If initialType is CUSTOM, go directly to custom-url, otherwise to input
          if (usesUrlSetupFlow(initialType)) {
            navigateTo.customUrl();
          } else {
            navigateTo.input();
          }
        }
        break;
      case 'select-type':
        // If type is CUSTOM, go to custom-url, otherwise to input
        if (usesUrlSetupFlow(type)) {
          navigateTo.customUrl();
        } else {
          navigateTo.input();
        }
        break;
      case 'input':
        // Navigate to address if Google type, otherwise to select-suggestion
        if (type === ProductType.GOOGLE) {
          navigateTo.address();
        } else {
          navigateTo.selectSuggestion();
        }
        break;
      case 'address':
        navigateTo.selectSuggestion();
        break;
      case 'select-suggestion':
      case 'custom-url':
        navigateTo.congrats();
        break;
      default:
        break;
    }
  };

  /**
   * Navigate to previous step based on current flow state
   */
  const goBack = (fromRoute: string) => {
    // If trying to go back from congrats page, redirect to target link instead
    if (fromRoute === 'congrats') {
      const targetLink = useSetupStore.getState().targetLink;
      window.location.href = targetLink || 'https://www.up2promo.com';
      return;
    }

    switch (fromRoute) {
      case 'email':
        navigateTo.welcome();
        break;
      case 'select-type':
        // Always go back to email from select-type (select-type only appears when no initialType)
        navigateTo.email();
        break;
      case 'input':
        // Go back to select-type if no initialType, otherwise to email
        if (!initialType) {
          navigateTo.selectType();
        } else {
          navigateTo.email();
        }
        break;
      case 'address':
        navigateTo.input();
        break;
      case 'select-suggestion':
        // Go back to address if Google type, otherwise to input
        if (type === ProductType.GOOGLE) {
          navigateTo.address();
        } else {
          navigateTo.input();
        }
        break;
      case 'custom-url':
        // Go back based on flow: if no initialType, go to select-type (user selected CUSTOM from select-type)
        // If initialType exists, go to email (CUSTOM was pre-selected via ?t=CU)
        // Note: If user switched from input to custom, they can use the "switch back" button instead
        if (!initialType) {
          navigateTo.selectType();
        } else {
          navigateTo.email();
        }
        break;
      default:
        break;
    }
  };

  return {
    basePath,
    navigateTo,
    goNext,
    goBack,
  };
}
